import { Skeleton, Tab, Tabs } from "@mui/material"
import { Subscribe } from "@react-rxjs/core"
import {
  ReactNode,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from "react"
import { useTranslation } from "react-i18next"

import {
  OrderType,
  toggleDirection,
  toggleOrderType,
  useTradeDirection,
} from "@/App/Equities/shared/services/orders"
import { Toggle } from "@/components/Toggle"
import { Box, Stack } from "@/generated/material"
import { Direction } from "@/generated/TradingGateway"

import { LimitOrderForm } from "./forms/LimitOrderForm"
import { MarketOrderForm } from "./forms/MarketOrderForm"
import { StopOrderForm } from "./forms/StopOrderForm"

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

interface OrderTab {
  key: string
  label: string
  content: ReactNode
  onSelect: () => void
}

const CustomTabPanel = ({ children, value, index }: TabPanelProps) => {
  return (
    <Stack
      role="tabpanel"
      sx={{
        display: value == index ? "flex" : "none",
      }}
    >
      {children}
    </Stack>
  )
}

interface OrderTabsProps {
  showDirectionToggle?: boolean
  centerTabs?: boolean
}

export const OrderTabs = ({
  showDirectionToggle = true,
  centerTabs = false,
}: OrderTabsProps) => {
  const { t } = useTranslation()

  const ORDER_TABS: OrderTab[] = useMemo(
    () => [
      {
        key: "market",
        label: t("orderTypeOptions.market"),
        content: <MarketOrderForm />,
        onSelect: () => toggleOrderType(OrderType.MarketOrder),
      },
      {
        key: "limit",
        label: t("orderTypeOptions.limit"),
        content: <LimitOrderForm />,
        onSelect: () => toggleOrderType(OrderType.LimitOrder),
      },
      {
        key: "stop",
        label: t("orderTypeOptions.stop"),
        content: <StopOrderForm />,
        onSelect: () => toggleOrderType(OrderType.StopOrder),
      },
    ],
    [t],
  )

  const [tabIndex, setTabIndex] = useState(0)

  const handleTabChange = useCallback(
    (_: SyntheticEvent, index: number) => {
      const tab = ORDER_TABS[index]
      tab.onSelect()
      setTabIndex(index)
    },
    [ORDER_TABS],
  )

  const direction = useTradeDirection()

  return (
    <>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        centered={centerTabs}
        sx={{
          ...(centerTabs && {
            height: "38px",
            marginBottom: "8px",
            "& .MuiTab-root": {
              height: "38px",
              minHeight: "38px",
              fontSize: "16px",
            },
          }),
        }}
      >
        {ORDER_TABS.map(({ label, key }) => (
          <Tab
            label={label}
            key={key}
            // Same selector as the MuiTab theme override, so sx wins by order
            sx={{
              "&.Mui-selected, &.Mui-selected:hover": { borderTop: "none" },
            }}
          />
        ))}
      </Tabs>

      {showDirectionToggle && (
        <Subscribe fallback={<Skeleton />}>
          <Box display="flex" paddingTop="2xl" paddingBottom="xl">
            <Toggle
              isToggled={direction === Direction.Sell}
              left="Buy"
              right="Sell"
              onChange={() =>
                toggleDirection(
                  direction === Direction.Buy ? Direction.Sell : Direction.Buy,
                )
              }
            />
          </Box>
        </Subscribe>
      )}
      {ORDER_TABS.map(({ key, content }, index) => (
        <CustomTabPanel value={tabIndex} index={index} key={key}>
          {content}
        </CustomTabPanel>
      ))}
    </>
  )
}
